import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertTriangle, PackageCheck } from "lucide-react";
import Button from "@/components/ui/Button";
import { getExeInfo, uploadAgentBuild } from "@/api/exe";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/dates";

const fmtBytes = (b) => {
  if (b == null) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
const fmtWhen = (iso) => (iso ? formatDateTime(iso) : null);
const KIND_LABEL = {
  "uploaded-exe": "Uploaded .exe",
  "packaged-exe": "Packaged .exe (dev box)",
  "source-zip": "Source zip (needs Python)",
};

// "Upload build" card: push a freshly-built aiqa-agent.zip so the dashboard Download button
// serves it to testers. Overwrites the previous one (single file) — no git, no redeploy.
export default function UploadAgentBuild() {
  // A superadmin publishes the platform default; a company admin publishes their
  // own company's. The server decides the same way from the authenticated user —
  // this only tells the reader which one they're about to replace.
  const { user } = useAuth();
  const isPlatform = user?.role === "superadmin";
  const [info, setInfo] = useState(null);
  const [pct, setPct] = useState(null); // null = idle, 0..100 = uploading
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const loadInfo = () => getExeInfo().then(setInfo).catch(() => {});
  useEffect(() => { loadInfo(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      setErr("Pick the .zip you built (dist_agent\\aiqa-agent.zip).");
      setMsg("");
      return;
    }
    setErr(""); setMsg(""); setPct(0);
    try {
      const r = await uploadAgentBuild(file, setPct);
      setMsg(`New build live — ${fmtBytes(r.size_bytes) || ""} · testers now download this.`);
      await loadInfo();
    } catch (e2) {
      setErr(e2.message || "Upload failed — the previous build is still active.");
    } finally {
      setPct(null);
    }
  };

  const uploading = pct !== null;
  const live = info && info.available;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-start gap-2 border-b border-slate-100 bg-gradient-to-br from-brand-50 to-white px-5 py-4 dark:border-white/10 dark:from-brand-500/10 dark:to-ink-900">
        <PackageCheck size={16} className="mt-0.5 shrink-0 text-brand-500" />
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Agent build (.exe)</h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Upload a freshly-built{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 font-mono dark:bg-white/10">aiqa-agent.zip</code>{" "}
            — the Download button then serves it to testers. It replaces the current one.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {isPlatform
              ? "You're publishing the PLATFORM build: every company that hasn't uploaded its own gets this one."
              : "You're publishing YOUR COMPANY's build: only your own testers get it. Other companies are unaffected."}
          </p>
        </div>
      </div>
      <div className="space-y-3 p-5">
        {/* current build */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium text-slate-600 dark:text-slate-300">Current build:</span>
          {live ? (
            <>
              <span className="rounded-full bg-brand-500/10 px-2 py-0.5 font-medium text-brand-600 dark:text-brand-300">
                {KIND_LABEL[info.kind] || info.kind}
              </span>
              {fmtBytes(info.size_bytes) && <span>· {fmtBytes(info.size_bytes)}</span>}
              {info.released_at && (
                <span title={formatDateTime(info.released_at)}>· Updated {fmtWhen(info.released_at)}</span>
              )}
            </>
          ) : (
            <span>none uploaded yet</span>
          )}
        </div>

        <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={onFile} />
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? (
              <><Loader2 size={15} className="animate-spin" /> Uploading… {pct}%</>
            ) : (
              <><Upload size={15} /> Upload build</>
            )}
          </Button>
          {!uploading && (
            <span className="text-xs text-slate-400">
              Pick dist_agent\aiqa-agent.zip (~354 MB) — takes a few minutes.
            </span>
          )}
        </div>

        {uploading && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
            <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        {msg && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-300">
            <CheckCircle2 size={14} /> {msg}
          </p>
        )}
        {err && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300">
            <AlertTriangle size={14} /> {err}
          </p>
        )}
      </div>
    </div>
  );
}
